import { FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminAssignPapers,
  adminCancelReviewAssignment,
  adminGetReviewer,
  adminResendAssignmentEmail,
  adminSearchAssignablePapers,
  type ConferencePaper,
  type ConferenceReviewAssignment,
  type ConferenceReviewerDetail,
} from "../api";
import { formatDateTime } from "../format";
import { PageHeader } from "../shells/PageHeader";

export function AdminAssignPapersPage() {
  const { reviewerId } = useParams<{ reviewerId: string }>();
  const { t, locale } = useLanguage();
  const [detail, setDetail] = useState<ConferenceReviewerDetail | null>(null);
  const [papers, setPapers] = useState<ConferencePaper[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [q, setQ] = useState("");
  const [language, setLanguage] = useState("");
  const [conferenceNumber, setConferenceNumber] = useState("");
  const [keywords, setKeywords] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reloadDetail = async () => {
    if (!reviewerId) return;
    const d = await adminGetReviewer(reviewerId, true);
    setDetail(d);
  };

  const searchPapers = async () => {
    if (!reviewerId) return;
    const rows = await adminSearchAssignablePapers(reviewerId, {
      q,
      language: language || undefined,
      conference_number: conferenceNumber ? Number(conferenceNumber) : null,
      keywords,
    });
    setPapers(rows);
    setSelected({});
  };

  useEffect(() => {
    if (!reviewerId) return;
    setBusy(true);
    Promise.all([adminGetReviewer(reviewerId, true), adminSearchAssignablePapers(reviewerId, {})])
      .then(([d, rows]) => {
        setDetail(d);
        setPapers(rows);
      })
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error))
      .finally(() => setBusy(false));
  }, [reviewerId, t.common.error]);

  const onSearch = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await searchPapers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onAssign = async () => {
    if (!reviewerId) return;
    const paper_ids = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (!paper_ids.length) {
      setError(t.admin.assignPapersNeedSelection);
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await adminAssignPapers(reviewerId, {
        paper_ids,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      });
      setMessage(t.admin.assignPapersDone);
      await reloadDetail();
      await searchPapers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async (row: ConferenceReviewAssignment) => {
    if (row.status === "cancelled") return;
    setBusy(true);
    setError(null);
    try {
      await adminCancelReviewAssignment(row.id);
      setMessage(t.admin.assignmentCancelled);
      await reloadDetail();
      await searchPapers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onResendEmail = async (row: ConferenceReviewAssignment) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await adminResendAssignmentEmail(row.id);
      setMessage(t.admin.assignmentEmailResent);
      await reloadDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const reminderLabel = (kind?: string | null) => {
    switch (kind) {
      case "reminder_7d":
        return t.admin.reminderStatus7d;
      case "reminder_3d":
        return t.admin.reminderStatus3d;
      case "reminder_due":
        return t.admin.reminderStatusDue;
      default:
        return "—";
    }
  };

  if (!detail && !error) return <p>{t.common.loading}</p>;
  if (!detail) {
    return (
      <div>
        <PageHeader
          title={t.admin.assignPapersTitle}
          backTo="/admin/reviewers"
          backLabel={t.admin.reviewers}
        />
        <div className="dn-alert dn-alert-error">{error || t.common.error}</div>
      </div>
    );
  }

  const rev = detail.reviewer;
  const activeAssignments = detail.assignments.filter((a) => a.status !== "cancelled");
  const cancelledAssignments = detail.assignments.filter((a) => a.status === "cancelled");

  return (
    <div>
      <PageHeader
        title={t.admin.assignPapersTitle}
        lead={t.admin.assignPapersLead}
        backTo="/admin/reviewers"
        backLabel={t.admin.reviewers}
      />

      <div className="dn-card" style={{ marginBottom: "1.25rem" }}>
        <h3>
          {rev.full_name}{" "}
          {rev.is_active ? <span className="dn-badge-reviewer">{t.admin.reviewerBadge}</span> : null}
        </h3>
        <p>
          {rev.email}
          <br />
          {rev.institution}
          <br />
          {t.admin.reviewerCode}: <code>{rev.registration_code}</code>
          <br />
          {t.admin.reviewerStatus}:{" "}
          <strong>{rev.is_active ? t.admin.reviewerActive : t.admin.reviewerInactive}</strong>
        </p>
        {!rev.is_active && (
          <div className="dn-alert dn-alert-error">{t.admin.assignPapersInactiveBlocked}</div>
        )}
      </div>

      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}

      <h2>{t.admin.currentAssignments}</h2>
      <div className="dn-table-wrap" style={{ marginBottom: "1.5rem" }}>
        <table className="dn-table">
          <thead>
            <tr>
              <th>{t.admin.assignColTitle}</th>
              <th>{t.admin.assignColAuthors}</th>
              <th>{t.admin.assignColLanguage}</th>
              <th>{t.admin.assignColAssigned}</th>
              <th>{t.admin.assignColDue}</th>
              <th>{t.admin.assignColEmail}</th>
              <th>{t.admin.assignColReminder}</th>
              <th>{t.admin.assignColStatus}</th>
              <th>{t.admin.actions}</th>
            </tr>
          </thead>
          <tbody>
            {activeAssignments.length === 0 ? (
              <tr>
                <td colSpan={9} className="dn-muted">
                  {t.admin.noAssignments}
                </td>
              </tr>
            ) : (
              activeAssignments.map((a) => (
                <tr key={a.id}>
                  <td>{a.title}</td>
                  <td>{a.authors}</td>
                  <td>{a.language.toUpperCase()}</td>
                  <td>{formatDateTime(a.assigned_at, locale)}</td>
                  <td>
                    {a.due_at ? formatDateTime(a.due_at, locale) : "—"}
                    {a.is_overdue ? (
                      <>
                        <br />
                        <strong style={{ color: "#8B1E1E" }}>{t.admin.overdue}</strong>
                      </>
                    ) : null}
                  </td>
                  <td>
                    {a.assignment_email_sent
                      ? t.admin.assignmentEmailSent
                      : t.admin.assignmentEmailNotSent}
                    {a.assignment_email_sent_at ? (
                      <>
                        <br />
                        <span className="dn-muted">
                          {formatDateTime(a.assignment_email_sent_at, locale)}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    {reminderLabel(a.latest_reminder_kind)}
                    {a.latest_reminder_sent_at ? (
                      <>
                        <br />
                        <span className="dn-muted">
                          {formatDateTime(a.latest_reminder_sent_at, locale)}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    {a.status === "completed"
                      ? t.reviews.statusCompleted
                      : a.status === "assigned"
                        ? t.reviews.statusAssigned
                        : a.status}
                  </td>
                  <td>
                    {a.status === "completed" || a.review?.status === "submitted" ? (
                      <Link
                        className="dn-btn dn-btn-ghost"
                        to={`/admin/review-assignments/${a.id}/review`}
                        style={{ marginInlineEnd: 8 }}
                      >
                        {t.admin.viewSubmittedReview}
                      </Link>
                    ) : null}
                    {a.status === "assigned" ? (
                      <>
                        <button
                          type="button"
                          className="dn-btn dn-btn-ghost"
                          disabled={busy}
                          onClick={() => onResendEmail(a)}
                          style={{ marginInlineEnd: 8 }}
                        >
                          {t.admin.resendAssignmentEmail}
                        </button>
                        <button
                          type="button"
                          className="dn-btn dn-btn-ghost"
                          disabled={busy}
                          onClick={() => onCancel(a)}
                        >
                          {t.admin.cancelAssignment}
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {cancelledAssignments.length > 0 && (
        <p className="dn-muted" style={{ marginBottom: "1.25rem" }}>
          {t.admin.cancelledAssignmentsCount.replace("{n}", String(cancelledAssignments.length))}
        </p>
      )}

      <h2>{t.admin.availablePapers}</h2>
      <form className="dn-cta-row" style={{ marginBottom: "1rem", flexWrap: "wrap" }} onSubmit={onSearch}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.admin.assignSearchPapers}
          aria-label={t.admin.assignSearchPapers}
          style={{ padding: "0.55rem 0.8rem", borderRadius: 10, border: "1px solid #ccc", minWidth: 220 }}
        />
        <select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label={t.admin.assignFilterLanguage}>
          <option value="">{t.admin.filterAllLanguages}</option>
          <option value="en">EN</option>
          <option value="ar">AR</option>
          <option value="fr">FR</option>
        </select>
        <input
          value={conferenceNumber}
          onChange={(e) => setConferenceNumber(e.target.value)}
          placeholder={t.admin.assignFilterEdition}
          aria-label={t.admin.assignFilterEdition}
          style={{ width: 90, padding: "0.55rem 0.6rem", borderRadius: 10, border: "1px solid #ccc" }}
        />
        <input
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder={t.admin.assignFilterKeywords}
          aria-label={t.admin.assignFilterKeywords}
          style={{ padding: "0.55rem 0.8rem", borderRadius: 10, border: "1px solid #ccc", minWidth: 160 }}
        />
        <button type="submit" className="dn-btn dn-btn-ghost" disabled={busy}>
          {t.admin.search}
        </button>
      </form>

      <div className="dn-cta-row" style={{ marginBottom: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
        <label>
          {t.admin.assignDueDate}
          <input
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            style={{ marginLeft: 8 }}
          />
        </label>
        <button
          type="button"
          className="dn-btn dn-btn-primary"
          disabled={busy || !rev.is_active}
          onClick={onAssign}
        >
          {t.admin.assignSelectedPapers}
        </button>
      </div>

      <div className="dn-table-wrap">
        <table className="dn-table">
          <thead>
            <tr>
              <th></th>
              <th>{t.admin.assignColTitle}</th>
              <th>{t.admin.assignColAuthors}</th>
              <th>{t.admin.assignColLanguage}</th>
              <th>{t.admin.assignColEdition}</th>
              <th>{t.admin.assignColKeywords}</th>
              <th>{t.admin.assignColPaperStatus}</th>
            </tr>
          </thead>
          <tbody>
            {papers.length === 0 ? (
              <tr>
                <td colSpan={7} className="dn-muted">
                  {t.admin.noAssignablePapers}
                </td>
              </tr>
            ) : (
              papers.map((p) => (
                <tr key={p.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!selected[p.id]}
                      disabled={!rev.is_active}
                      onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))}
                    />
                  </td>
                  <td>{p.title}</td>
                  <td>{p.authors}</td>
                  <td>{p.language.toUpperCase()}</td>
                  <td>{p.conference_number ?? "—"}</td>
                  <td>{p.keywords || "—"}</td>
                  <td>{p.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
